import Papa from 'papaparse'
import RestaurantsApi from '../api/restaurants'
import PoolsApi from '../api/pools'
import RestaurantPoolScheduleApi from '../api/restaurantpoolschedule'
import moment from '../helpers/moment'
import { addErrorNotification } from './notifications'
import { getDefaultTimezone } from './system'

export const CSV_PARSE_RESET = 'restaurantPoolSchedule/CSV_PARSE_RESET'
export const CSV_PARSE_STARTED = 'restaurantPoolSchedule/CSV_PARSE_STARTED'
export const CSV_PARSE_COMPLETED = 'restaurantPoolSchedule/CSV_PARSE_COMPLETED'

export const SCHEDULE_LOAD_RESET = 'restaurantPoolSchedule/SCHEDULE_LOAD_RESET'
export const SCHEDULE_LOAD_STARTED = 'restaurantPoolSchedule/SCHEDULE_LOAD_STARTED'
export const SCHEDULE_LOAD_COMPLETED = 'restaurantPoolSchedule/SCHEDULE_LOAD_COMPLETED'
export const SCHEDULE_LOAD_ERROR = 'restaurantPoolSchedule/SCHEDULE_LOAD_ERROR'

export const SCHEDULE_SUBMIT_STARTED = 'restaurantPoolSchedule/SCHEDULE_SUBMIT_STARTED'
export const SCHEDULE_SUBMIT_COMPLETED = 'restaurantPoolSchedule/SCHEDULE_SUBMIT_COMPLETED'
export const SCHEDULE_SUBMIT_ERROR = 'restaurantPoolSchedule/SCHEDULE_UPLOAD_ERROR'

export const cancelCSVInput = () => {
  return dispatch => {
    dispatch({ type: CSV_PARSE_RESET })
  }
}

export const submitSchedule = (validScheduleRows) => {
  return async dispatch => {
    dispatch({ type: SCHEDULE_SUBMIT_STARTED, validScheduleRows: validScheduleRows})

    try {
      const requestData = validScheduleRows.map(row => {
        return {
          restaurantId: row.restaurant.id,
          poolId: row.pool.id,
          startDateUtc: row.startDateUtc
        }
      })
      const response = await RestaurantPoolScheduleApi.Upload({ data: requestData })
      const json = await response.json()
      const scheduleUploadResult = json.resultFields.map(f => {
        return {
          "restaurant": {"id": f.restaurantId},
          "pool": { "id": f.poolId },
          "startDateUtc": f.startDateUtc,
          "status": f.status,
          "message": f.message
        }
      })
      return dispatch({ type: SCHEDULE_SUBMIT_COMPLETED, scheduleUploadResult: scheduleUploadResult })
    }
    catch (err) {
      console.log(`Error: ${err.message}`)
      return dispatch({ type: SCHEDULE_SUBMIT_ERROR, scheduleUploadError: err.message })
    }
  }
}

export const resetScheduleLoad = () => {
  return dispatch => {
    dispatch({ type: SCHEDULE_LOAD_RESET })
  }
}

export const adjustScheduleStartTime = (startDate) => {
  if(!startDate.isValid())
    return startDate

  const now = moment.tz(startDate.tz())
  const isToday = startDate.isSame(now, 'day')
  return isToday ? now : startDate.clone().hour(3).minute(0).second(0)
}

export const processCSV = (csvString) => {
  return (dispatch, getState) => {

    dispatch({ type: CSV_PARSE_STARTED, csvParseRaw: csvString })

    const result = Papa.parse(csvString, {skipEmptyLines:true})

    // PapaParse won't check field count unless there is a header row;
    // we do not have a header row so we need to check field count explicitly
    const expectedNumberOfColumns = 3
    appendFieldMismatchToPapaParseResult(result, expectedNumberOfColumns)

    dispatch({ type: CSV_PARSE_COMPLETED, csvParseRaw: csvString, csvParseResult: result })

    //parsing the CSV has some errors so can not continue processing
    if(result.errors.length > 0)
      return Promise.resolve()

    const scheduleRows =  result.data.map((datum, i) => ({
      id: i,
      restaurant: { id:datum[0].trim() },
      pool: { name:datum[1].trim() },
      startDateUtc: datum[2].trim(),
    }))

    return updateScheduleRows(scheduleRows)(dispatch, getState)
  }
}

export const updateScheduleRows = (scheduleRows) => {
  return (dispatch, getState) => {
    dispatch({ type: SCHEDULE_LOAD_STARTED, scheduleRows: scheduleRows})

    const state = getState()
    const defaultTimeZone = getDefaultTimezone(state)

    return Promise.all([
      resolveRestaurantDataFromIds(scheduleRows.map(row => row.restaurant.id)),
      resolvePoolDataFromNames(scheduleRows.map(row => row.pool.name))
    ]).then(([restaurants, pools]) => scheduleRows.map((row, i) => {

      const startDateMoment = moment.tz(row.startDateUtc, [moment.ISO_8601, 'DD/MM/YYYY'], true, pools[i].timeZone || defaultTimeZone)
      const startDateTimeMoment = adjustScheduleStartTime(startDateMoment)

      return {
        id: row.id,
        restaurant: restaurants[i],
        pool: pools[i],
        startDateUtc: isStartDateMomentValid(startDateTimeMoment) ? startDateTimeMoment.format() : startDateTimeMoment.creationData().input,
        isRestaurantValid: !!restaurants[i].name,
        isPoolValid: !!pools[i].id || pools[i].name.trim() === '',
        isStartDateUtcValid: isStartDateMomentValid(startDateTimeMoment)
      }
    })
    ).then(augmentedRows => {
      dispatch({ type: SCHEDULE_LOAD_COMPLETED, scheduleRows: augmentedRows})
    }).catch((err)=>{
      dispatch({ type: SCHEDULE_LOAD_ERROR, scheduleError: err.message})
      dispatch(addErrorNotification("Error resolving schedule data", err.message))
    })
  }
}

const resolveRestaurantDataFromIds = (restaurantIds) => {
  const numberIds = restaurantIds.filter(id=>/^[0-9]+$/.test(id))
  const restaurantPromise = numberIds.length === 0
    ? Promise.resolve([])
    : RestaurantsApi.GetByIds(numberIds)
      .then(response => response.json())
      .then(json => json.restaurants)

  return restaurantPromise
    .then(restaurants=> restaurantIds.map(restaurantId => restaurants.find(x => x.id + "" === restaurantId + "") || {id: restaurantId}))
}

const resolvePoolDataFromNames = (poolNames) => {
  return PoolsApi.GetAll()
    .then(response => response.json())
    .then(json => json.items)
    .then(pools => poolNames.map(poolName => pools.find(x => x.name === poolName) || {name: poolName}))
}

const isStartDateMomentValid = (startDateMoment) =>{
  const mustBeAfter = moment.tz(startDateMoment.tz()).startOf('day')
  const mustBeBefore = mustBeAfter.clone().add(1, 'year')
  return startDateMoment
    && startDateMoment.isValid()
    && startDateMoment.isAfter(mustBeAfter)
    && startDateMoment.isBefore(mustBeBefore)
}

const appendFieldMismatchToPapaParseResult = (result, expetedFieldCount) => {
  result.data.forEach((datum, index)=>{
    if (datum.length < expetedFieldCount) {
      result.errors.push({
        row: index,
        type:"FieldMismatch",
        code:"TooFewFields",
        message: `Too few fields: expected ${expetedFieldCount} fields but parsed ${datum.length}`
      })
    }
    else if (datum.length > expetedFieldCount) {
      result.errors.push({
        row: index,
        type:"FieldMismatch",
        code:"TooManyFields",
        message: `Too many fields: expected ${expetedFieldCount} fields but parsed ${datum.length}`
      })
    }
  })
  return result
}



// WEBPACK FOOTER //
// ./src/actions/restaurantPoolSchedule.js